/**
 * What the navigation gutter contains, decided as data.
 *
 * The gutter was four hard-coded `<Link>`s, each computing its own destination
 * inline. Two of them are project-scoped and have to fall back when no project
 * has resolved, which was two near-identical `projectId ? … : orgRootPath(team)`
 * expressions sitting in the middle of JSX. That decision is the only thing
 * about this gutter that can be wrong, so it lives in one pure function here
 * and the renderer draws whatever comes back.
 *
 * Modelled on the desktop's `navGutterItems.ts`: an entry is `{ id, section,
 * icon, label, hideable }`, sections render in a fixed order, and availability
 * is decided once at build time by a conditional spread so an unavailable item
 * never enters the registry at all. The console does not (yet) persist a
 * user-hidden set, so `hideable` is carried but not consumed — it is what the
 * desktop's customize popover reads, and declaring it now is what keeps the two
 * registries the same shape.
 *
 * Deliberately nothing beyond these. Settings lives in the profile menu and
 * organization management is a dialog, not a nav level; both are settled.
 *
 * `to` is a path rather than a route object because the gutter is the only
 * consumer and every destination is already a `routing.ts` helper.
 */

import type { TeamSummary } from '../api';
import {
  orgAdminPath,
  orgDocsPath,
  orgRequestsPath,
  orgRootPath,
  orgTrackersPath,
  type ConsoleMode,
} from '../routing';

/**
 * Top-to-bottom order. `workspace` is the team's shared work; `activity` is what
 * is waiting on the reader; `administration` is the team itself.
 */
export type NavGutterSection = 'workspace' | 'activity' | 'administration';

export const NAV_GUTTER_SECTION_ORDER: readonly NavGutterSection[] = [
  'workspace',
  'activity',
  'administration',
];

export interface NavGutterItem {
  /** Doubles as the mode this item is active for. */
  id: ConsoleMode;
  section: NavGutterSection;
  /** Material Symbols ligature name. */
  icon: string;
  label: string;
  /** The expanded-gutter label; the collapsed rail shows `icon` and `label` as a title. */
  to: string;
  /**
   * Whether a future customize affordance would be allowed to remove it. False
   * for the item that is the only way back to the rest of the console.
   */
  hideable: boolean;
  /** A count badge; only rendered above zero. */
  badgeCount?: number;
}

export interface NavGutterHost {
  team: TeamSummary;
  /**
   * The project Shared Docs and Trackers open into, or null when none has
   * resolved yet. Both items stay in the gutter and fall back to the org root:
   * with no project there is no truthful room to name, but the item is still
   * how a member gets there once one exists.
   */
  projectId: string | null;
  unreadRequests: number;
  /**
   * Inside the phone app the console is a Wiki and Trackers reader: Requests
   * and Organization are left to the desktop and the full console.
   */
  embedded?: boolean;
}

/**
 * The gutter for one signed-in member of one team.
 *
 * Pure, and the only place that decides what is reachable — the renderer draws
 * whatever comes back and adds nothing of its own.
 */
export function buildNavGutterItems({
  team,
  projectId,
  unreadRequests,
  embedded = false,
}: NavGutterHost): NavGutterItem[] {
  const scoped = projectId !== null && projectId !== '' ? projectId : null;
  const items: NavGutterItem[] = [
    {
      id: 'docs',
      section: 'workspace',
      icon: 'public',
      label: 'Wiki',
      to: scoped ? orgDocsPath(team, scoped) : orgRootPath(team),
      // The console's home. Hiding it would leave a member of a single-project
      // team with nowhere to land.
      hideable: false,
    },
    {
      id: 'trackers',
      section: 'workspace',
      icon: 'database',
      label: 'Trackers',
      to: scoped ? orgTrackersPath(team, scoped) : orgRootPath(team),
      hideable: true,
    },
    {
      id: 'requests',
      section: 'activity',
      icon: 'ballot',
      label: 'Requests',
      to: orgRequestsPath(team),
      hideable: true,
      badgeCount: unreadRequests,
    },
    {
      id: 'organization',
      section: 'administration',
      icon: 'domain',
      label: 'Organization',
      to: orgAdminPath(team),
      hideable: true,
    },
  ];
  return embedded ? items.filter((item) => item.id === 'docs' || item.id === 'trackers') : items;
}

/** Groups the registry for rendering, dropping sections that came back empty. */
export function groupNavGutterItems(
  items: readonly NavGutterItem[],
): Array<{ section: NavGutterSection; items: NavGutterItem[] }> {
  return NAV_GUTTER_SECTION_ORDER
    .map((section) => ({ section, items: items.filter((item) => item.section === section) }))
    .filter((group) => group.items.length > 0);
}

/**
 * Where the brand mark points. Falls back to the org root, which resolves the
 * member's landing surface rather than assuming one.
 */
export function navGutterHomePath(team: TeamSummary, projectId: string | null): string {
  return projectId ? orgDocsPath(team, projectId) : orgRootPath(team);
}
